import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Body, Button, Caption, Row, Stack } from '@leanin/ui';

import {
  useUpdateBroadcast,
  type UpdateBroadcastInput,
} from '@/lib/mutations/admin';
import {
  useUpdateGroupBroadcast,
  type UpdateGroupBroadcastInput,
} from '@/lib/mutations/groups';
import { useStepUp } from '@/lib/use-step-up';
import {
  broadcastKeys,
  type AdminBroadcastDetail,
  type BroadcastDoc,
  type BroadcastScope,
} from '@/lib/queries/broadcasts';
import { emptyBroadcastDoc } from './BroadcastBodyEditor';
import {
  BroadcastEditorFields,
  resolveAudience,
  type AudienceState,
  type BroadcastAudienceContext,
} from './BroadcastEditorFields';
import { ScheduleSendButton } from './ScheduleSendButton';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

export interface BroadcastEditFormProps {
  readonly audience: BroadcastAudienceContext;
  readonly draft: AdminBroadcastDetail;
}

const EMPTY_AUDIENCE: AudienceState = {
  kind: 'all',
  circles: [],
  networks: [],
  members: [],
  recentDays: 30,
};

export function BroadcastEditForm({ audience, draft }: BroadcastEditFormProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const updatePlatform = useUpdateBroadcast();
  const updateGroup = useUpdateGroupBroadcast();
  const { runStepUp } = useStepUp();

  const initialBodyDoc =
    (draft.bodyDoc as BroadcastDoc | undefined) ?? emptyBroadcastDoc();

  const [subject, setSubject] = useState(draft.subject);
  const [preheaderText, setPreheaderText] = useState(draft.preheaderText ?? '');
  const [bodyDoc, setBodyDoc] = useState<BroadcastDoc>(initialBodyDoc);
  const [audienceState, setAudienceState] =
    useState<AudienceState>(EMPTY_AUDIENCE);
  const [replyTo, setReplyTo] = useState(draft.replyTo ?? '');
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [savePending, setSavePending] = useState(false);

  const [version, setVersion] = useState(draft.version);

  const pending = updatePlatform.isPending || updateGroup.isPending;

  const scope: BroadcastScope =
    audience.mode === 'platform'
      ? { kind: 'platform' }
      : {
          kind: 'group',
          groupKind: audience.kind,
          groupId: audience.groupId,
        };

  const dirty =
    subject !== draft.subject ||
    preheaderText !== (draft.preheaderText ?? '') ||
    replyTo !== (draft.replyTo ?? '') ||
    JSON.stringify(bodyDoc) !== JSON.stringify(initialBodyDoc);

  const guard = useUnsavedChangesGuard({ dirty });

  const minimumFieldsForSave =
    subject.trim().length > 0 && !docIsEmpty(bodyDoc);

  // 'ok' completed; 'cancelled' = user dismissed the step-up reverification
  // prompt (platform send/schedule only); 'error' carries the failure code
  // (for version-conflict detection) + message.
  type SubmitResult =
    | { status: 'ok' }
    | { status: 'cancelled' }
    | { status: 'error'; code: string; message: string };

  const submit = async (
    mode: 'send' | 'schedule' | 'draft',
    scheduledIso?: string,
  ): Promise<SubmitResult> => {
    if (subject.trim().length === 0) {
      return { status: 'error', code: 'validation', message: 'Subject is required.' };
    }
    if (docIsEmpty(bodyDoc)) {
      return { status: 'error', code: 'validation', message: 'Message is required.' };
    }

    const preheaderValue =
      preheaderText.trim().length > 0 ? preheaderText.trim() : null;
    const replyToValue = replyTo.trim().length > 0 ? replyTo.trim() : null;
    // 'send' = send now → schedule for the current instant. The server flips
    // status draft→scheduled off a non-null scheduledSendAt and the dispatcher
    // picks it up immediately (mirrors the composer's create-with-mode='send'
    // path). Previously 'send' fell through to `undefined`, so the PATCH
    // omitted scheduledSendAt and the draft's status never changed.
    const scheduledSendAt =
      mode === 'schedule'
        ? scheduledIso
        : mode === 'draft'
          ? null
          : new Date().toISOString();
    const fallback =
      mode === 'draft'
        ? 'Failed to save.'
        : mode === 'schedule'
          ? 'Failed to schedule.'
          : 'Failed to send.';

    // A platform-wide send (draft→scheduled) is step-up'd server-side (RBAC
    // Decision C); runStepUp opens Clerk's reverification modal and retries
    // once on success. Group-leader sends carry no step-up — runStepUp is a
    // transparent pass-through there (the server never returns the hint), so
    // both branches share one normalised outcome.
    const mutateOutcome =
      audience.mode === 'platform'
        ? await runStepUp(() => {
            const { audienceSpec } = resolveAudience(audienceState);
            const patch: UpdateBroadcastInput = {
              version,
              subject: subject.trim(),
              bodyDoc,
              preheaderText: preheaderValue,
              replyTo: replyToValue,
              audienceSpec,
              ...(scheduledSendAt !== undefined ? { scheduledSendAt } : {}),
            };
            return updatePlatform.mutateAsync({ id: draft.id, patch });
          }, fallback)
        : await runStepUp(() => {
            const input: UpdateGroupBroadcastInput = {
              kind: audience.kind,
              groupId: audience.groupId,
              broadcastId: draft.id,
              version,
              subject: subject.trim(),
              bodyDoc,
              preheaderText: preheaderValue,
              replyTo: replyToValue,
              ...(scheduledSendAt !== undefined ? { scheduledSendAt } : {}),
            };
            return updateGroup.mutateAsync(input);
          }, fallback);

    if (mutateOutcome.status === 'cancelled') return { status: 'cancelled' };
    if (mutateOutcome.status === 'error') {
      return {
        status: 'error',
        code: mutateOutcome.code,
        message: mutateOutcome.message,
      };
    }
    setVersion(mutateOutcome.data.version);
    return { status: 'ok' };
  };

  const handleVersionConflict = async (code: string) => {
    if (/version_mismatch/i.test(code)) {
      setError('This broadcast was modified in another tab. Reloading…');
      await qc.invalidateQueries({
        queryKey: broadcastKeys.detail(scope, draft.id).queryKey,
      });
      return true;
    }
    return false;
  };

  const navigateAway = () => {
    guard.allowNextNavigation();
    if (audience.mode === 'platform') {
      void navigate({ to: '/admin/broadcasts' });
    } else {
      window.history.back();
    }
  };

  const handleSendNow = async () => {
    setError(null);
    const result = await submit('send');
    if (result.status === 'ok') return navigateAway();
    if (result.status === 'cancelled') return;
    if (await handleVersionConflict(result.code)) return;
    setError(result.message);
  };

  const handleSchedule = async (iso: string) => {
    setError(null);
    const result = await submit('schedule', iso);
    if (result.status === 'ok') return navigateAway();
    if (result.status === 'cancelled') return;
    if (await handleVersionConflict(result.code)) return;
    setError(result.message);
  };

  const handleGuardSave = async () => {
    setDialogError(null);
    setSavePending(true);
    const result = await submit('draft');
    setSavePending(false);
    if (result.status === 'ok') return guard.proceed();
    if (result.status === 'cancelled') return;
    if (await handleVersionConflict(result.code)) return;
    setDialogError(result.message);
  };

  const handleCancel = () => {
    if (audience.mode === 'platform') {
      void navigate({ to: '/admin/broadcasts' });
    } else {
      window.history.back();
    }
  };

  return (
    <Stack gap="lg">
      <BroadcastEditorFields
        audience={audience}
        subject={subject}
        onSubjectChange={setSubject}
        preheaderText={preheaderText}
        onPreheaderChange={setPreheaderText}
        bodyDoc={bodyDoc}
        onBodyDocChange={setBodyDoc}
        audienceState={audienceState}
        onAudienceStateChange={setAudienceState}
        replyTo={replyTo}
        onReplyToChange={setReplyTo}
        broadcastIdForPreview={draft.id}
        disabled={pending}
      />

      {error !== null ? (
        <Body size="sm" color="destructive">
          {error}
        </Body>
      ) : null}

      <Row gap="md" align="center" justify="space-between">
        <Caption>
          Version {version} · last updated{' '}
          {new Date(draft.createdAt).toLocaleString()}
        </Caption>
        <Row gap="md" align="center">
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <ScheduleSendButton
            disabled={!minimumFieldsForSave}
            pending={pending}
            onSendNow={handleSendNow}
            onSchedule={handleSchedule}
          />
        </Row>
      </Row>

      <UnsavedChangesDialog
        open={guard.dialogOpen}
        onCancel={guard.reset}
        onSave={() => void handleGuardSave()}
        onDiscard={guard.proceed}
        saveDisabled={!minimumFieldsForSave}
        saveDisabledReason={
          !minimumFieldsForSave
            ? 'Subject and message are required to save as draft.'
            : undefined
        }
        savePending={savePending}
        error={dialogError}
      />
    </Stack>
  );
}

function docIsEmpty(doc: BroadcastDoc): boolean {
  const blocks = doc.content as ReadonlyArray<{
    content?: ReadonlyArray<{ type?: string }>;
  }>;
  if (blocks.length === 0) return true;
  // A lone hardBreak (Shift+Enter on an empty line) carries no content —
  // same notion as the server's docToText, which projects it to whitespace.
  return blocks.every((block) => {
    const inline = block.content;
    return (
      inline === undefined ||
      inline.every((node) => node.type === 'hardBreak')
    );
  });
}

